import { Locale, localeCompare } from '@ardoq/locale';
import { IconName } from '@ardoq/icons';
import { getJoinedTspanText } from '@ardoq/dom-utils';
import { ImageContentType } from '@ardoq/api-types';
import { ContentControls, NodeOption } from './types';

export const isShowControls = (controls?: ContentControls) =>
  controls?.center || controls?.zoomIn || controls?.zoomOut || controls?.reset;

const getBpmnShapeId = (bpmnShapeType: string | undefined): string => {
  // TODO more types?
  // https://www.lucidchart.com/pages/bpmn-symbols-explained
  // https://process-analytics.github.io/bpmn-visualization-js/#supported-bpmn-elements
  switch (bpmnShapeType) {
    case 'Exclusive (XOR) Gateway':
      return 'bpmn-gateway-xor';
    case 'Task':
      return 'bpmn-task';
    case 'Collapsed Subprocess':
      return 'bpmn-subprocess-collapsed';
    case 'End Event':
      return 'bpmn-end-event';
    case 'Start Event':
      return 'bpmn-start-event';
    case 'Data Object':
      return 'bpmn-data-object';
    case 'Intermediate Timer Event':
      return 'bpmn-intermediate-event-catch-timer';
    case 'Lane':
      return IconName.SPLIT_SCREEN; // TODO something horizontal?
    case 'Intermediate Escalation Event':
      return 'bpmn-intermediate-event-catch-escalation';
    default:
      return IconName.CODE;
  }
};

const getSvgElementType = (element?: SVGElement): string | undefined =>
  element?.querySelector('title')?.textContent || undefined;

const getSvgBusinessProcessElements = (svg: string | null, locale: Locale) => {
  if (!svg) {
    return [];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(svg, 'image/svg+xml');
  const textElements = doc.querySelectorAll('text');

  return [...textElements]
    .map(textElement => {
      return {
        id: textElement?.id,
        label: getJoinedTspanText(textElement),
        type: getSvgElementType(
          textElement.parentElement as unknown as SVGElement
        ),
      };
    })
    .filter(({ label, id }) => label && id)
    .sort((a, b) => localeCompare(a.type + a.label, b.type + b.label, locale));
};

/**
 * Parse svg to get available zoom in options.
 * This logic applies only for BPMN diagrams, mostly from Signavio integration,
 * but can be expanded for other integrations if they follow same pattern
 */
const getSvgZoomInOptions = (src: string, locale: Locale) =>
  getSvgBusinessProcessElements(src, locale).map(element => ({
    id: element.id,
    label: element.label,
    shapeId: getBpmnShapeId(element.type),
    type: element.type,
  }));

export const getZoomInOptions = (
  src: string,
  contentType: ImageContentType,
  locale: Locale
): NodeOption[] => {
  if (contentType === ImageContentType.SVG) {
    return getSvgZoomInOptions(src, locale);
  }

  return [];
};
